import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { IconButton, Icons } from 'utoniq-ui'

import { Item, SkuUnit } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { encodeSkuId } from '@/core/sku'

import { ApplyPreSaleFirstComeFirstServedValue } from '../FirstComeFirstServedDetailForm'

type SkuUnitSelectModalProps = {
  isBeforeReceiption: boolean
  item: Partial<Item>
  selectedItems: ApplyPreSaleFirstComeFirstServedValue['choices']
  onClose: () => void
  onGoBack: () => void
  onSelectItem: (skuId: string | undefined) => void
}

export const SkuUnitSelectModal: Organism<SkuUnitSelectModalProps> = ({
  isBeforeReceiption,
  item,
  onClose,
  onGoBack,
  onSelectItem,
}) => {
  const { t } = useTranslation('lotteryDetail')
  const { router, generatePath, routerPages } = useRouter()

  const skuUnits = item?.skuSettings?.units

  return (
    <OrganismFrame
      sx={{
        bgcolor: 'background.default',
        height: '75dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" sx={{ py: 4, px: 3 }} alignItems="center">
        <IconButton.Back size="small" onClick={onGoBack} />
        <Typography variant="h6" align="center" width="100%">
          {t('selectTicketTitle', 'チケットを選択')}
        </Typography>
        <IconButton.Close size="small" onClick={onClose} />
      </Stack>
      <Divider />
      <Typography
        variant="h5"
        width="100%"
        color="text.secondary"
        sx={{ bgcolor: 'grey.50', px: 5, py: 1 }}
      >
        {t('performanceInformation', '公演情報')}
      </Typography>
      <Typography variant="body2" width="100%" sx={{ px: 5, py: 2 }}>
        {item?.title}
      </Typography>
      <Typography
        variant="h5"
        width="100%"
        color="text.secondary"
        sx={{ bgcolor: 'grey.50', px: 5, py: 1, mt: 3 }}
      >
        {t('typeSelection', '種類選択')}
      </Typography>
      <Stack
        flex={1}
        spacing={2}
        sx={{ p: 5, overflowY: 'auto', ...styleSets.hideScrollbar() }}
      >
        {skuUnits?.map(itemSku => {
          const purchaseAvailableNumber =
            (itemSku?.stock ?? 0) -
            ((itemSku?.purchasedNumber ?? 0) +
              (itemSku?.pendingPurchaseNumber ?? 0))

          const isSoldOut = checkSoldOut(itemSku)

          const resaleAvailable = checkResaleAvailable(item)

          return (
            <Stack key={itemSku?.id}>
              <Card
                elevation={0}
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <CardActionArea
                  disabled={isSoldOut || isBeforeReceiption}
                  onClick={() => onSelectItem(itemSku.id)}
                >
                  <CardContent sx={{ px: 5, py: 2, '&:last-child': { pb: 4 } }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="subtitle1"
                        color={
                          isSoldOut || isBeforeReceiption
                            ? 'text.disabled'
                            : 'text.primary'
                        }
                      >
                        {itemSku?.unitName}
                      </Typography>

                      <SkuUnitStatusCell amount={purchaseAvailableNumber} />
                    </Stack>
                    {isSoldOut && resaleAvailable && (
                      <Box
                        sx={{
                          width: '100%',
                          height: '20px',
                        }}
                      />
                    )}
                  </CardContent>
                </CardActionArea>
                {isSoldOut && resaleAvailable && (
                  <Stack justifyContent="flex-end" alignItems="end">
                    <Button
                      variant="text"
                      onClick={() => {
                        router.push({
                          pathname: generatePath(
                            routerPages.ResaleApplicationDetail,
                            {
                              itemId: item?.id ?? '#',
                            }
                          ),
                          search: `${
                            itemSku?.id
                              ? `?skuId=${encodeSkuId(itemSku.id)}`
                              : ''
                          }`,
                        })
                      }}
                      sx={{
                        p: 0,
                        position: 'absolute',
                        right: '20px',
                        bottom: '8px',
                        zIndex: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="primary.main">
                        {t('toResale', 'リセール情報に移動する')}
                      </Typography>
                      <Icons.RightArrow
                        color="primary"
                        fontSize="xSmall"
                        sx={{ ml: 2 }}
                      />
                    </Button>
                  </Stack>
                )}
              </Card>
            </Stack>
          )
        })}
      </Stack>
    </OrganismFrame>
  )
}
type SkuUnitStatusCellProps = {
  amount: number
}
const SkuUnitStatusCell: Cell<SkuUnitStatusCellProps> = ({ amount }) => {
  const { t } = useTranslation('firstComeFirstServedDetail')

  if (amount >= 10) {
    return (
      <Typography variant="subtitle1" color="text.secondary">
        {t('onSale')}
      </Typography>
    )
  }

  if (amount >= 1 && amount < 10) {
    return (
      <Typography variant="subtitle1" color="text.secondary">
        {t('fewLeft')}
      </Typography>
    )
  }

  return (
    <Typography variant="subtitle1" color="error.main">
      {t('soldOutLabel')}
    </Typography>
  )
}

const checkSoldOut = (itemSku: Partial<SkuUnit>) => {
  return itemSku?.stock !== null
    ? itemSku?.stock ===
        (itemSku?.purchasedNumber ?? 0) + (itemSku?.pendingPurchaseNumber ?? 0)
    : false
}

const checkResaleAvailable = (item: Partial<Item>) => {
  return (
    item?.resaleEnabled &&
    item?.metadata?.ticket?.resalePeriod?.endedAt >
      DateTime.nowUnixMilliseconds() &&
    item?.metadata?.ticket?.resalePeriod?.startedAt <
      DateTime.nowUnixMilliseconds()
  )
}
